import React, { useEffect, useMemo, useState } from "react";
import { X, Search, Folder, Check, ImageOff } from "lucide-react";
import api, { resolveMediaUrl } from "@/lib/api";
import { SafeImg } from "@/components/site/Reveal";

// Selettore immagini dalla libreria Media CMS (miniature reali, filtro cartella e ricerca).
export default function MediaPicker({ title = "Scegli un'immagine dalla libreria", current = "", onPick, onClose, allowClear = true, testId = "media-picker" }) {
  const [items, setItems] = useState([]);
  const [folder, setFolder] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/media").then((r) => setItems(r.data.filter((m) => m.media_type === "image"))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const folders = useMemo(() => Array.from(new Set(items.map((m) => m.folder).filter(Boolean))).sort(), [items]);
  const shown = items.filter((m) => (!folder || m.folder === folder) && (!q || m.title.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="fixed inset-0 z-[110] bg-obsidian/92 backdrop-blur-sm overflow-y-auto p-4 sm:p-8" data-testid={testId}>
      <div className="max-w-4xl mx-auto card-dark p-5 sm:p-7">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-copper">Libreria immagini</p>
            <h2 className="font-serif text-2xl text-cream mt-1">{title}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-sand hover:text-cream shrink-0" data-testid={`${testId}-close`}><X className="h-5 w-5" /></button>
        </div>

        <div className="flex flex-wrap gap-3 mb-5">
          <label className="relative flex-1 min-w-[180px]">
            <Search className="h-4 w-4 text-sand absolute left-3 top-1/2 -translate-y-1/2" />
            <input className="field !pl-9" placeholder="Cerca per titolo…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testId}-search`} />
          </label>
          {folders.length > 0 && (
            <label className="relative min-w-[200px]">
              <Folder className="h-4 w-4 text-sand absolute left-3 top-1/2 -translate-y-1/2" />
              <select className="field !pl-9" value={folder} onChange={(e) => setFolder(e.target.value)} data-testid={`${testId}-folder`}>
                <option value="">Tutte le cartelle</option>
                {folders.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>
          )}
          {allowClear && current && (
            <button type="button" onClick={() => onPick("", null)} className="btn-ghost !px-4 !py-2 !text-xs !text-red-300 hover:!border-red-400" data-testid={`${testId}-clear`}>
              <ImageOff className="h-4 w-4" /> Rimuovi immagine
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-sand text-sm">Carico la libreria…</p>
        ) : shown.length === 0 ? (
          <p className="text-sand text-sm" data-testid={`${testId}-empty`}>Nessuna immagine trovata: caricane una nella sezione Media CMS.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto pr-1" data-testid={`${testId}-grid`}>
            {shown.map((m) => {
              const sel = current && current === m.url;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => onPick(m.url, m)}
                  className={"group relative text-left border transition-colors " + (sel ? "border-copper" : "border-white/10 hover:border-copper/60")}
                  data-testid={`${testId}-item-${m.id}`}
                >
                  <SafeImg src={resolveMediaUrl(m.url)} alt={m.title} className="w-full aspect-[4/3] object-cover" />
                  {sel && <span className="absolute top-2 right-2 h-6 w-6 bg-copper text-obsidian flex items-center justify-center"><Check className="h-4 w-4" /></span>}
                  <span className="block p-2 text-xs text-cream2 truncate">{m.title}</span>
                  {m.folder && <span className="block px-2 pb-2 font-mono text-[9px] uppercase tracking-widest text-sand truncate">{m.folder}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Campo "immagine" con miniatura e pulsante per aprire il selettore.
export function ImageField({ label, value, onChange, testId, hint }) {
  const [open, setOpen] = useState(false);
  const src = resolveMediaUrl(value);
  return (
    <div data-testid={testId}>
      {label && <label className="label">{label}</label>}
      <div className="flex items-center gap-4 bg-obsidian2 border border-white/10 p-3">
        <div className="h-20 w-28 shrink-0 bg-obsidian border border-white/10 overflow-hidden flex items-center justify-center">
          {src ? <img src={src} alt="" className="h-full w-full object-cover" data-testid={`${testId}-thumb`} /> : <ImageOff className="h-5 w-5 text-sand" data-testid={`${testId}-thumb-empty`} />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-cream2 truncate" data-testid={`${testId}-value`}>{value ? value.split("/").pop() : "Nessuna immagine selezionata"}</p>
          {hint && <p className="text-[11px] text-sand mt-0.5">{hint}</p>}
          <div className="flex flex-wrap gap-2 mt-2">
            <button type="button" onClick={() => setOpen(true)} className="btn-ghost !px-3 !py-1.5 !text-xs" data-testid={`${testId}-open`}>Scegli dalla libreria</button>
            {value && <button type="button" onClick={() => onChange("")} className="btn-ghost !px-3 !py-1.5 !text-xs !text-red-300 hover:!border-red-400" data-testid={`${testId}-clear`}>Rimuovi</button>}
          </div>
        </div>
      </div>
      {open && <MediaPicker current={value} onPick={(url) => { onChange(url); setOpen(false); }} onClose={() => setOpen(false)} testId={`${testId}-picker`} />}
    </div>
  );
}
